import { MUSIC_MAP, DEFAULT_MUSIC_VOLUME, type MusicEntry } from '@/data/musicMap'

// シーンIDは動的（scene データ由来の string）でも渡ってくるため、
// ルックアップ用に string キーの Record として参照する。
const musicMap: Record<string, string | MusicEntry> = MUSIC_MAP

const audioCache = new Map<string, HTMLAudioElement>()
const musicStack: string[] = []

let currentAudio: HTMLAudioElement | null = null
let currentName: string | null = null
let pauseRequested = false
let enabled = true

function getAudio(name: string): HTMLAudioElement | null {
  const _entry = musicMap[name]
  if (!_entry) {
    console.warn(`Music ${name} not found.`)
    return null
  }

  const entry: MusicEntry = typeof _entry === 'string' ? { filename: _entry } : _entry

  const src = `/musics/${entry.filename}`
  let audio = audioCache.get(src)

  if (!audio) {
    audio = new Audio(src)
    audio.loop = entry.loop ?? true
    audio.volume = entry.volume ?? DEFAULT_MUSIC_VOLUME
    audioCache.set(src, audio)
  }

  return audio
}

function createMusicManager() {
  function playMusic(name: string): void {
    if (currentName === name && currentAudio && !currentAudio.paused) {
      return
    }

    stopMusic()

    const audio = getAudio(name)
    if (!audio) {
      console.warn(`Failed to get audio for ${name}`)
      return
    }

    currentAudio = audio!
    currentName = name

    if (enabled) {
      audio.play().then(() => {
        if (pauseRequested) {
          audio.pause()
        }
      }).catch((err) => {
        console.warn(`Failed to play music ${name}`, err)
      })
    }
  }

  function stopMusic(): void {
    if (currentAudio) {
      currentAudio.pause()
      currentAudio.currentTime = 0
      currentAudio = null
      currentName = null
    }
  }

  function pauseMusic(): void {
    pauseRequested = true
    currentAudio?.pause()
  }

  function resumeMusic(): void {
    pauseRequested = false
    if (currentAudio?.paused && enabled) {
      currentAudio.play().catch((err) => {
        console.warn(`Failed to resume music ${currentName}`, err)
      })
    }
  }

  function pushMusic(name: string): void {
    if (currentName) {
      musicStack.push(currentName)
    }
    playMusic(name)
  }

  function popMusic(): void {
    const previous = musicStack.pop()
    if (previous) {
      playMusic(previous)
    }
    else {
      stopMusic()
    }
  }

  function enableMusic(): void {
    enabled = true
    resumeMusic()
  }

  function disableMusic(): void {
    enabled = false
    pauseMusic()
  }

  return {
    playMusic,
    stopMusic,
    pauseMusic,
    resumeMusic,
    isPauseRequested: () => pauseRequested,
    pushMusic,
    popMusic,
    enableMusic,
    disableMusic,
    isEnabled: () => enabled,
  }
}

let _singleton: ReturnType<typeof createMusicManager> | null = null

export function useMusic() {
  if (!_singleton) {
    _singleton = createMusicManager()
  }
  return _singleton
}

export function useMusicToggle() {
  const music = useMusic()
  const enabled = ref(music.isEnabled())

  // 再生状態が変化したら enabled を更新
  watchEffect(() => {
    enabled.value = music.isEnabled()
  })

  // チェックボックス側からの変更に応じて pause/resume を実行
  watchEffect(() => {
    if (enabled.value) {
      music.enableMusic()
    }
    else {
      music.disableMusic()
    }
  })

  return enabled
}
